import { Formio } from '@formio/js'
import { user } from "../app"
import { map } from "../constants"
import { getAudioDuration, serverUrl, uploadFileToTemp } from "../server/advayta"
import { deleteByPath } from "../upload"

const HtmlComponent = Formio.Components.components.container

class FileComponent extends HtmlComponent {
    static fileComponents = [];
    /**
     * Определяет схему компонента.
     * @returns {Object} Схема компонента.
     */
    schema() {
        return {
            type: 'filefield',
            key: 'filefield',
            label: 'Файл',
            addAnotherPosition: 'top',
            input: true,
            components: [],
        }
    }

    /**
     * Конструктор компонента.
     * @param {Object} component Конфигурация компонента.
     * @param {Object} options Опции компонента.
     * @param {Object} data Данные для инициализации компонента.
     */
    constructor(component, options, data) {
        super(component, options, data)
        this.components = []
        this.filesToDelete = [] // Массив для хранения файлов, которые нужно удалить
        this.originalValue = null // Исходное значение файла
        FileComponent.fileComponents.push(this)
    }

    /**
     * Инициализация компонента.
     */
    init() {
        super.init()
        this.originalValue = this.data[this.path] || ''
    }

    /**
     * Генерирует HTML-код компонента.
     * @returns {string} HTML-компонент.
     */
    render() {
        return super.render(this.renderTemplate())
    }

    /**
     * Генерирует шаблон для отображения компонента.
     * @returns {string} HTML-шаблон.
     */
    renderTemplate() {
        if (typeof this.data[this.path] === 'object') this.data[this.path] = ''
        const isEditor = user && user.role === map.roles.editor
        const buttonsVisibility = isEditor ? '' : 'd-none'

        return `
            <div ref="component" class="file-row mb-2 formio-form-group has-feedback formio-component formio-component-filefield formio-component-${this.component.key} ${this.component?.validate?.required ? 'required' : ''}">
                <label class="col-form-label ${this.component?.validate?.required ? 'field-required' : ''}">${this.component.label}</label>
                <div class="d-flex gap-2">
                      <input type="text" class="form-control" value="${this.data[this.path]}" />
                      <button class="btn btn-secondary btn-sm upload-file-btn ${buttonsVisibility}" style="min-width: max-content">Загрузить</button>
                      <button class="btn btn-danger btn-sm delete-file-btn ${buttonsVisibility}" style="min-width: max-content">Удалить</button>
                </div>
                <div style="font-size: 14px; color: green; display: none" class="file-uploading">Загрузка файла...</div>
            </div>
        `
    }

    /**
     * Привязывает обработчики событий к элементам DOM.
     * @param {HTMLElement} element Элемент DOM, к которому нужно привязать события.
     */
    attach(element) {
        super.attach(element)

        const uploadButton = element.querySelector('.upload-file-btn')
        const deleteButton = element.querySelector('.delete-file-btn')
        if (uploadButton) {
            uploadButton.addEventListener('click', this.uploadFile.bind(this))
        }
        if (deleteButton) {
            deleteButton.addEventListener('click', this.deleteFile.bind(this))
        }

        this.input = element.querySelector('input')
        this.input.addEventListener('change', () => {
            this.updateValue()
        })
    }

    /**
     * Возвращает текущее значение компонента.
     * @returns {Array} Массив объектов с данными строк.
     */
    getValue() {
        return this.dataValue || []
    }

    /**
     * Обновляет текущее значение компонента.
     * @returns {Array} Массив объектов с обновленными данными строк.
     */
    updateValue() {
        this.dataValue = this.element.querySelector('input').value
        return this.dataValue
    }

    /**
     * Устанавливает новое значение для компонента.
     * @param {Array} value Массив объектов, содержащих новые данные строк.
     */
    setValue(value) {
        this.dataValue = value
        this.redraw()
    }

    /**
     * Обрабатывает загрузку файла.
     * @param {Event} event Событие клика на кнопку загрузки файла.
     */
    async uploadFile(event) {
        event.preventDefault()
        const fileInput = document.createElement('input')
        fileInput.type = 'file'
        if (this.component.properties?.accept) {
            fileInput.accept = this.component.properties.accept
        }

        fileInput.addEventListener('change', async (fileEvent) => {
            const file = fileInput.files[0]
            if (file) {
                try {
                    const currentValue = this.element.querySelector('input').value

                    // Если есть текущий файл, добавляем его в список для удаления
                    if (currentValue) {
                        this.filesToDelete.push(currentValue)
                    }

                    event.target.closest('.file-row').querySelector('.file-uploading').style.display = 'block'
                    const upload = await uploadFileToTemp(file)
                    if (upload) {
                        const newFilePath = `${serverUrl}/upload/${upload[0].name}`
                        this.element.querySelector('input').value = newFilePath
                        this.updateValue()
                        getAudioDuration(newFilePath).then(result => {
                            if (this.root.getComponent('audioDuration')) {
                                this.root.getComponent('audioDuration').setValue(this.secondsToHMS(result))
                            }
                        })
                    } else {
                        console.error('Ошибка при загрузке файла')
                    }
                } catch (error) {
                    console.error('Ошибка при загрузке файла:', error)
                } finally {
                    event.target.closest('.file-row').querySelector('.file-uploading').style.display = 'none'
                }
            }
        })

        fileInput.click()
    }

    /**
     * Удаляет файл
     * @param {Event} event Событие клика на кнопку удаления файла.
     * @param {boolean} immediate Удалить файл сразу (true) или добавить в очередь на удаление (false)
     */
    async deleteFile(event, immediate = false) {
        event.preventDefault()
        const input = event.target.closest('.file-row').querySelector('input')
        if (!input.value) return

        if (!immediate && !confirm('Вы действительно хотите удалить файл?')) {
            return
        }

        const path = input.value

        if (immediate) {
            await deleteByPath(path)
        } else {
            this.filesToDelete.push(path)
        }

        input.value = ''
        this.updateValue()
    }

    /**
     * Вызывается перед сохранением, чтобы удалить старые файлы
     */
    async processFilesToDelete() {
        try {
            // Удаляем только если значение изменилось
            const currentValue = this.getValue()
            if (this.originalValue && this.originalValue !== currentValue) {
                this.filesToDelete.push(this.originalValue)
            }

            // Удаляем все файлы из списка на удаление
            for (const filePath of this.filesToDelete) {
                try {
                    await deleteByPath(filePath)
                } catch (error) {
                    console.error(`Ошибка при удалении файла ${filePath}:`, error)
                }
            }

            this.filesToDelete = [] // Очищаем список
        } catch (error) {
            console.error('Ошибка при обработке файлов для удаления:', error)
        }
    }

    static async processAllFilesToDelete() {
        for (const component of FileComponent.fileComponents) {
            await component.processFilesToDelete()
        }
        // Очищаем список после обработки
        FileComponent.fileComponents = []
    }

    secondsToHMS(seconds) {
        seconds = Number(seconds)

        const hours = Math.floor(seconds / 3600)
        const minutes = Math.floor((seconds % 3600) / 60)
        const secs = Math.floor(seconds % 60)

        const pad = (num) => num.toString().padStart(2, '0')

        return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`
    }
}

export default FileComponent